import { Chip } from "@exopengithub/exokit";
import { IconArrowRight } from "@tabler/icons-react";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { DashboardWidgetBase } from "./DashboardWidgetBase.js";
import { blogUrl, type NewsItem, type NewsLocale } from "./newsFeed.js";

interface NewsWidgetViewProps {
	items: NewsItem[];
	locale: NewsLocale;
	isLoading?: boolean;
	isError?: boolean;
}

export const NewsWidgetView = ({
	items,
	locale,
	isLoading = false,
	isError = false,
}: NewsWidgetViewProps) => {
	const { t } = useTranslation();
	const sourceLabel = (sourceType: NewsItem["sourceType"]) =>
		sourceType === "knowledge-bank" ? t("Knowledge bank") : t("Blog");

	return (
		<DashboardWidgetBase
			title={t("News & releases")}
			action={
				<a
					href={blogUrl(locale)}
					target="_blank"
					rel="noopener noreferrer"
					className="flex items-center gap-2 hover:underline"
				>
					{t("All news")}
					<IconArrowRight size={16} />
				</a>
			}
			flush
		>
			{isLoading ? (
				<p className="p-6 text-sm text-grey-500">{t("Loading")}</p>
			) : isError ? (
				<p className="p-6 text-sm text-gray-600">{t("Could not load news.")}</p>
			) : items.length === 0 ? (
				<p className="p-6 text-sm text-gray-600">{t("No news yet.")}</p>
			) : (
				<ul>
					{items.map((item) => (
						<li
							key={item.id}
							className="border-b border-grey-200 last:border-b-0"
						>
							<a
								href={item.url}
								target="_blank"
								rel="noopener noreferrer"
								className="group flex items-start gap-3 px-6 py-4 hover:bg-grey-50"
							>
								<span className="min-w-0 grow">
									<span className="block truncate text-sm font-semibold text-grey-900 group-hover:underline">
										{item.title}
									</span>
									<span className="mt-1 block text-xs text-grey-500">
										{sourceLabel(item.sourceType)} ·{" "}
										{format(parseISO(item.publishedAt), "yyyy-MM-dd")}
									</span>
								</span>
								{item.tag && (
									<Chip
										variant={item.tagVariant}
										size="small"
										className="shrink-0 uppercase"
									>
										{item.tag}
									</Chip>
								)}
							</a>
						</li>
					))}
				</ul>
			)}
		</DashboardWidgetBase>
	);
};
